import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Text from '../Text/Text';
import './Header.css';
import veripayLogo from '../../images/VeriPay-Negative.jpg';

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  helpUrl?: string; // Optional prop to override default help URL
}

// Default help documentation URL
const DEFAULT_HELP_URL = 'https://www.veripay.com.au/help/';

const Header: React.FC<HeaderProps> = ({
  title,
  showBackButton = true,
  onBackClick,
  helpUrl = DEFAULT_HELP_URL
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  
  // Handle logout
  const handleLogout = () => {
    navigate('/logout');
  };
  
  // Handle help button click
  const handleHelpClick = () => {
    window.open(helpUrl, '_blank', 'noopener,noreferrer');
  };
  
  // Determine title based on current route if not provided
  const getTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path.includes('/home')) return '';
    if (path.includes('/pay')) return 'Pay Someone';
    if (path.includes('/tracking/add')) return 'Package Tracking';
    if (path.includes('/tracking')) return 'Tracking Status';
    if (path.includes('/notifications')) return 'Notifications';
    if (path.includes('/payment')) return 'Payment Summary';
    if (path.includes('/transactions')) return 'Transactions';
    if (path.includes('/profile')) return 'Profile';
    if (path.includes('/disputes')) return 'Dispute';
    
    return 'VeriPay';
  };
  
  // Handle back button click
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };
  
  // Don't show back button on home screen
  const isHome = location.pathname.includes('/home');
  const shouldShowBackButton = showBackButton && !isHome;
  
  return (
    <div className="header">
      <div className="header-content">
        {shouldShowBackButton && (
          <button className="back-button" onClick={handleBackClick}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      
        {isHome && (
          <button className="logout-button" onClick={handleLogout}>
            <span className="logout-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17l5-5-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="logout-text">Logout</span>
          </button>
        )}
        <Text className="text-h2">{getTitle()}</Text>
        
        {/* Help button */}
        <button className="help-button" onClick={handleHelpClick} aria-label="Help">
          <span className="help-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none">
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" strokeWidth="2" strokeLinecap="round" />
              <line x1="12" y1="17" x2="12" y2="17" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
        </button>
      </div>
      <div className="veripay-logo-container">
        <div className="veripay-logo">
          <img src={veripayLogo} alt="VeriPay Logo" />
        </div>
        {isAdmin && (
          <div className="admin-link-container">
            <button
              className="admin-link"
              onClick={() => navigate('/admin')}
            >
              Admin
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;